import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useCallback, useRef } from "react";
import { useClickAway } from "react-use";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { HEADLESS_UI_POPOVER_ROOT } from "../../../uikit/PopoverPortal";

export interface LeftSidebarProps extends ComponentPropsWithoutRef<"aside"> {
  children: ReactNode;
  className?: string;
  onClickAway?: () => void;
}

export const LeftSidebar = ({
  children,
  className,
  onClickAway,
  ...rest
}: LeftSidebarProps) => {
  const ref = useRef<HTMLElement>(null);
  const closeSidebarView = useBuilderStore(state => state.closeSidebarView);
  const isActionSidebarOpen = useBuilderStore(
    state => state.isActionSidebarOpen,
  );

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // Note: Do not close the sidebar if the click is within the Headless UI portal root
      if (!(event.target instanceof Element)) {
        return;
      }
      const target = event.target;
      const portalRoot = document.getElementById("headlessui-portal-root");
      const popoverRoot = document.getElementById(HEADLESS_UI_POPOVER_ROOT);

      if (portalRoot?.contains(target) || popoverRoot?.contains(target)) {
        return;
      }

      if (onClickAway) {
        onClickAway();
        return;
      }
      if (!isActionSidebarOpen) return;
      closeSidebarView();
    },
    [closeSidebarView, onClickAway, isActionSidebarOpen],
  );

  useClickAway(ref, handleClickAway);

  return (
    <aside
      ref={ref}
      className={classNames(
        "relative z-50 h-full w-16 [grid-area:left-sidebar]",
        className,
      )}
      {...rest}
    >
      {children}
    </aside>
  );
};
